import React from 'react'
import App from 'next/app'
import Head from 'next/head'
import { ApolloProvider } from '@apollo/client'
import createApolloClient from './apollo-client'
import cookies from 'js-cookie'
import getConfig from 'next/config'
import getLocaleFromHostname from '~/utils/i18n/getLocaleFromHostname'
import parse from 'url-parse'
const { TOKEN_COOKIE_NAME, API_URL } = getConfig().publicRuntimeConfig
const { host } = parse(API_URL)

// On the client, we store the Apollo Client in the following
// variable. This prevents the client from reinitializing between page
// transitions.
let globalApolloClient = null

/**
 * Passes along the context object and adds a few custom helpers.
 * @param {NextPageContext | NextAppContext} ctx
 * @param props
 */
const getContext = (ctx, props = {}) => ({
  ...(ctx ? ctx : {}),
  getForwardedHeaders: () => {
    let headers = {}

    if (ctx) {
      /*
        Make sure to only include a header if header has value. Because we check
        for ctx we know that req is always defined. If the header has value
        we spread ...it on the headers object.
      */
      headers = {
        ...(ctx.req.headers['user-agent'] && { 'user-agent': ctx.req.headers['user-agent'] }),
        ...(ctx.req.headers['x-forwarded-for'] && {
          'x-forwarded-for': ctx.req.headers['x-forwarded-for'],
        }),
        ...(ctx.req.headers['x-forwarded-port'] && {
          'x-forwarded-port': ctx.req.headers['x-forwarded-port'],
        }),
        ...(ctx.req.headers['x-forwarded-proto'] && {
          'x-forwarded-proto': ctx.req.headers['x-forwarded-proto'],
        }),
        ...(getLocaleFromHostname(ctx.req.headers.host) && {
          'accept-language': getLocaleFromHostname(ctx.req.headers.host),
        }),
        host,
      }
    }

    return headers
  },
  getToken: () => {
    return ctx ? ctx.req.cookies[TOKEN_COOKIE_NAME] : cookies.getJSON(TOKEN_COOKIE_NAME)
  },
  getPath: () => {
    return ctx ? ctx.req.originalUrl : window.location.pathname
  },
  getLanguage: () => {
    return ctx ? getLocaleFromHostname(ctx.req.headers.host) : props.languageFromRequest
  },
})

/**
 * Installs the Apollo Client on NextPageContext or NextAppContext.
 * Useful if you want to use apolloClient inside getStaticProps,
 * getStaticPaths or getServerSideProps.
 * @param {NextPageContext | NextAppContext} ctx
 */
export const initOnContext = ctx => {
  const inAppContext = Boolean(ctx.ctx)

  // Initialize ApolloClient if not already done
  const apolloClient =
    ctx.apolloClient ||
    initApolloClient(ctx.apolloState || {}, getContext(inAppContext ? ctx.ctx : ctx))

  // We send the Apollo Client as a prop to the component to avoid
  // calling initApollo() twice in the server. Otherwise, the
  // component would have to call initApollo() again but this time
  // without the context. Once that happens, the following code will
  // make sure we send the prop as `null` to the browser.
  apolloClient.toJSON = () => null

  // Add apolloClient to NextPageContext & NextAppContext. This allows
  // us to consume the apolloClient inside our custom
  // `getInitialProps({ apolloClient })`.
  ctx.apolloClient = apolloClient
  if (inAppContext) {
    ctx.ctx.apolloClient = apolloClient
  }

  return ctx
}

/**
 * Always creates a new apollo client on the server Creates or reuses
 * apollo client in the browser.
 * @param  {NormalizedCacheObject} initialState
 * @param  {NextPageContext} ctx
 */
const initApolloClient = (initialState, ctx) => {
  // Make sure to create a new client for every server-side request so
  // that data isn't shared between connections (which would be bad).
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, ctx)
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, ctx)
  }

  return globalApolloClient
}

/**
 * Creates a withApollo HOC that provides the apolloContext to a
 * next.js Page or AppTree.
 * @param  {Object} withApolloOptions
 * @param  {Boolean} [withApolloOptions.ssr=false]
 * @returns {(PageComponent: ReactNode) => ReactNode}
 */
export const withApollo = ({ ssr = false } = {}) => PageComponent => {
  const WithApollo = ({ apolloClient, apolloState, ...pageProps }) => {
    let client
    if (apolloClient) {
      // Happens on: getDataFromTree & Next.js SSR
      client = apolloClient
    } else {
      // Happens on: Next.js CSR
      client = initApolloClient(apolloState, getContext(null, pageProps))
    }

    return (
      <ApolloProvider client={client}>
        <PageComponent {...pageProps} />
      </ApolloProvider>
    )
  }

  // Set the correct displayName in development.
  if (process.env.NODE_ENV !== 'production') {
    const displayName = PageComponent.displayName || PageComponent.name || 'Component'
    WithApollo.displayName = `withApollo(${displayName})`
  }

  if (ssr || PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async ctx => {
      const inAppContext = Boolean(ctx.ctx)
      const { apolloClient } = initOnContext(ctx)

      // Run wrapped getInitialProps methods.
      let pageProps = {}
      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx)
      } else if (inAppContext) {
        pageProps = await App.getInitialProps(ctx)
      }

      // Only on the server:
      if (typeof window === 'undefined') {
        const { AppTree } = ctx
        // When redirecting, the response is finished. No point in
        // continuing to render.
        if (
          // The official example does not check for `inAppContext`
          // here, but this broke redirects in getInitialProps of page
          // components for us.
          inAppContext ? ctx.ctx.res && ctx.ctx.res.finished : ctx.res && ctx.res.finished
        ) {
          return pageProps
        }

        // Only if dataFromTree is enabled.
        if (ssr && AppTree) {
          try {
            // Import `@apollo/react-ssr` dynamically. We don't want
            // to have this in our client bundle.
            const { getDataFromTree } = await import('@apollo/client/react/ssr')

            // Since AppComponents and PageComponents have different
            // context types we need to modify their props a little.
            let props
            if (inAppContext) {
              props = { ...pageProps, apolloClient }
            } else {
              props = { pageProps: { ...pageProps, apolloClient } }
            }

            // Take the Next.js AppTree, determine which queries are
            // needed to render, and fetch them. This method can be
            // pretty slow since it renders your entire AppTree once
            // for every query. Check out apollo fragments if you want
            // to reduce the number of rerenders.
            // https://www.apollographql.com/docs/react/data/fragments/
            await getDataFromTree(<AppTree {...props} />)
          } catch (error) {
            // Prevent Apollo Client GraphQL errors from crashing SSR.
            // Handle them in components via the data.error prop:
            // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
            console.error(
              'Error while running `getDataFromTree` for',
              inAppContext ? ctx.ctx.req.path : ctx.req.path,
              error
            )
          }

          // getDataFromTree does not call componentWillUnmount head
          // side effect therefore need to be cleared manually
          Head.rewind()
        }
      }

      return {
        ...pageProps,
        // Extract query data from the Apollo store.
        apolloState: apolloClient.cache.extract(),
        // Provide the client for ssr. As soon as this payload gets
        // JSON.stringified it will remove itself.
        apolloClient: ctx.apolloClient,
      }
    }
  }

  return WithApollo
}
